<template>
  <div class="hotspot-layer" ref="hotspotLayer">
    <!-- Hotspot markers rendered as HTML overlays positioned via 3D projection -->
    <div
      v-for="hs in visibleHotspots"
      :key="hs.id"
      class="hotspot-marker"
      :class="[`hotspot-${hs.type}`, { 'hotspot-active': activePopover === hs.id, 'hotspot-gazing': gazingId === hs.id }]"
      :style="hs.style"
      @click="handleClick(hs)"
      @mouseenter="handleHover(hs)"
      @mouseleave="handleLeave(hs)"
    >
      <div class="hotspot-icon" v-html="getIcon(hs.icon, hs.type)"></div>
      <span v-if="hs.title" class="hotspot-label">{{ hs.title }}</span>
    </div>

    <!-- Popover — centered overlay (no Teleport — stays in fullscreen context) -->
      <div v-if="activePopover && popoverData" class="hotspot-popover-backdrop" @click="activePopover = null">
        <div class="hotspot-popover" @click.stop>
          <div class="popover-header">
            <span class="popover-title">{{ popoverData.title || '' }}</span>
            <button @click="activePopover = null" class="popover-close">×</button>
          </div>
          <div
            v-if="popoverData.content"
            class="popover-body"
            v-html="popoverData.contentHtml ? sanitize(popoverData.content) : escapeHtml(popoverData.content)"
          ></div>
          <a
            v-if="popoverData.type === 'link' && popoverData.url"
            :href="popoverData.url"
            :target="popoverData.urlNewTab !== false ? '_blank' : '_self'"
            rel="noopener"
            class="popover-link"
          >
            {{ popoverData.url }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
            </svg>
          </a>
        </div>
      </div>

    <!-- VR gaze crosshair + dwell ring — centered, only in vrMode -->
    <div v-if="vrMode" class="vr-crosshair">
      <svg class="vr-dwell-ring" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle class="dwell-track" cx="20" cy="20" r="17"/>
        <circle
          class="dwell-fill"
          cx="20" cy="20" r="17"
          :style="{ strokeDashoffset: dwellRingOffset }"
        />
      </svg>
      <div class="crosshair-dot"/>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import type { Hotspot } from '../types'
import * as THREE from 'three'

const props = defineProps<{
  hotspots: Hotspot[]
  camera: THREE.PerspectiveCamera | null
  containerEl: HTMLElement | null
  sphereRadius?: number
  cameraChanged?: number // increment this prop to trigger position update
  vrMode?: boolean // enable gaze-based dwell interaction
}>()

const emit = defineEmits<{
  navigateScene: [sceneId: string]
}>()

const hotspotLayer = ref<HTMLElement>()
const activePopover = ref<string | null>(null)
const popoverPosition = ref({ x: 0, y: 0 })

const radius = computed(() => props.sphereRadius || 500)

interface VisibleHotspot extends Hotspot {
  style: Record<string, string>
  screenX: number
  screenY: number
}

const visibleHotspots = ref<VisibleHotspot[]>([])
const needsUpdate = ref(true)

// ── Gaze dwell (VR mode) ─────────────────────────────────────────────────────
const DWELL_MS = 1800        // ms to hold gaze before triggering
const GAZE_PX = 72           // max distance from screen center to count as gazing
const RING_CIRC = 2 * Math.PI * 17  // circumference of SVG dwell ring (r=17)

const gazingId = ref<string | null>(null)
const dwellProgress = ref(0)          // 0–100
let dwellStart: number | null = null

const dwellRingOffset = computed(() =>
  RING_CIRC * (1 - dwellProgress.value / 100)
)

function checkGaze() {
  if (!props.vrMode || !props.containerEl) {
    if (gazingId.value) { gazingId.value = null; dwellProgress.value = 0 }
    return
  }
  if (visibleHotspots.value.length === 0) return
  const cx = props.containerEl.clientWidth / 2
  const cy = props.containerEl.clientHeight / 2

  let closest: VisibleHotspot | null = null
  let closestDist = Infinity
  for (const hs of visibleHotspots.value) {
    const dist = Math.hypot(hs.screenX - cx, hs.screenY - cy)
    if (dist < closestDist) { closestDist = dist; closest = hs }
  }

  if (closest && closestDist < GAZE_PX) {
    if (gazingId.value !== closest.id) {
      gazingId.value = closest.id
      dwellStart = performance.now()
      dwellProgress.value = 0
    } else {
      const pct = Math.min(100, ((performance.now() - (dwellStart ?? 0)) / DWELL_MS) * 100)
      dwellProgress.value = pct
      if (pct >= 100) {
        const target = closest
        gazingId.value = null
        dwellProgress.value = 0
        dwellStart = null
        handleClick(target)
      }
    }
  } else {
    if (gazingId.value) { gazingId.value = null; dwellProgress.value = 0; dwellStart = null }
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const popoverData = computed(() => {
  if (!activePopover.value) return null
  return props.hotspots.find(h => h.id === activePopover.value) || null
})

// Popover is now centered — no position needed

// Convert pitch/yaw to 3D position on sphere
// Legacy iPanorama uses yaw convention opposite to Three.js — negate yaw to match
function pitchYawToVector(pitch: number, yaw: number): THREE.Vector3 {
  const pitchRad = THREE.MathUtils.degToRad(pitch)
  const yawRad = THREE.MathUtils.degToRad(-yaw) // Negate to match camera convention
  const r = radius.value * 0.98 // slightly inside sphere

  return new THREE.Vector3(
    r * Math.cos(pitchRad) * Math.sin(yawRad),
    r * Math.sin(pitchRad),
    r * Math.cos(pitchRad) * Math.cos(yawRad)
  )
}

// Project 3D position to screen coordinates
function projectToScreen(position: THREE.Vector3): { x: number; y: number; behind: boolean } | null {
  if (!props.camera || !props.containerEl) return null

  const vector = position.clone().project(props.camera)
  const behind = vector.z > 1

  const rect = props.containerEl.getBoundingClientRect()
  const x = (vector.x * 0.5 + 0.5) * rect.width
  const y = (-vector.y * 0.5 + 0.5) * rect.height

  return { x, y, behind }
}

function updatePositions() {
  if (!props.camera || !props.containerEl || !props.hotspots.length) {
    visibleHotspots.value = []
    return
  }

  visibleHotspots.value = props.hotspots
    .map(hs => {
      // Support both storage formats:
      //   New builder:  { x, y, z }  (3-D world coords on sphere r=500)
      //   Legacy:       { pitch, yaw } (spherical degrees)
      const pos = hs.position as any
      const pos3d = (pos.x !== undefined && pos.y !== undefined && pos.z !== undefined)
        ? new THREE.Vector3(Number(pos.x), Number(pos.y), Number(pos.z))
        : pitchYawToVector(pos.pitch, pos.yaw)
      const screen = projectToScreen(pos3d)
      if (!screen || screen.behind) return null

      return {
        ...hs,
        screenX: screen.x,
        screenY: screen.y,
        style: {
          left: `${screen.x}px`,
          top: `${screen.y}px`,
          transform: 'translate(-50%, -50%)',
          position: 'absolute' as const,
        },
      } as VisibleHotspot
    })
    .filter((hs): hs is VisibleHotspot => hs !== null)

  needsUpdate.value = false
}

// Watch for camera changes from parent
watch(() => props.cameraChanged, () => {
  needsUpdate.value = true
})

// Watch for hotspots array changes
watch(() => props.hotspots, () => {
  needsUpdate.value = true
}, { deep: true })

// Performance-optimized: only update when camera actually moves
// Use throttled updates instead of continuous RAF
let rafId: number | null = null
let lastCameraState = ''
let animationFrameCount = 0

function getCameraState(): string {
  if (!props.camera) return ''
  const pos = props.camera.position
  const quat = props.camera.quaternion
  return `${pos.x.toFixed(3)},${pos.y.toFixed(3)},${pos.z.toFixed(3)},${quat.x.toFixed(3)},${quat.y.toFixed(3)},${quat.z.toFixed(3)},${quat.w.toFixed(3)}`
}

function tick() {
  // Update if camera moved OR hotspots changed (scene switch with same initial view)
  const currentState = getCameraState()
  if (currentState !== lastCameraState || needsUpdate.value) {
    lastCameraState = currentState
    updatePositions() // resets needsUpdate inside
  }

  // Gaze dwell check runs every frame in VR mode
  if (props.vrMode) checkGaze()

  // Skip frames to reduce load (update every 2nd frame = ~30fps instead of 60fps)
  animationFrameCount++
  if (animationFrameCount % 2 === 0) {
    rafId = requestAnimationFrame(tick)
  } else {
    rafId = requestAnimationFrame(tick)
  }
}

onMounted(() => {
  updatePositions() // Initial positioning
  tick() // Start optimized loop
})

// Reset gaze on container resize (prevents stale cx/cy after orientation change)
let resizeObserver: ResizeObserver | null = null
onMounted(() => {
  if (props.containerEl && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (gazingId.value) {
        gazingId.value = null
        dwellProgress.value = 0
        dwellStart = null
      }
    })
    resizeObserver.observe(props.containerEl)
  }
})
onUnmounted(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

onUnmounted(() => {
  if (rafId !== null) cancelAnimationFrame(rafId)
})

function handleClick(hs: VisibleHotspot) {
  if (hs.type === 'scene' && hs.targetSceneId) {
    emit('navigateScene', hs.targetSceneId)
  } else if (hs.type === 'link' && hs.url) {
    if (hs.urlNewTab !== false) {
      window.open(hs.url, '_blank', 'noopener')
    } else {
      window.location.href = hs.url
    }
  } else {
    // info, video, or any type with content → toggle popover
    activePopover.value = activePopover.value === hs.id ? null : hs.id
    popoverPosition.value = { x: hs.screenX, y: hs.screenY }
  }
}

function handleHover(hs: VisibleHotspot) {
  if (hs.type === 'info' && hs.content) {
    activePopover.value = hs.id
    popoverPosition.value = { x: hs.screenX, y: hs.screenY }
  }
}

function handleLeave(_hs: VisibleHotspot) {
  // Keep popover open on click-triggered ones
}

// Icon SVGs for different hotspot types and custom icons
function getIcon(icon: string | undefined, type: string): string {
  const icons: Record<string, string> = {
    'info': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>',
    'question': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>',
    'link': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>',
    'eye': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>',
    'arrow-right': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>',
    'arrow-left': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>',
    'arrow-up': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"/></svg>',
    'arrow-down': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"/></svg>',
    'home': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>',
    'camera': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12m-3.2 0a3.2 3.2 0 1 0 6.4 0a3.2 3.2 0 1 0-6.4 0M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z"/></svg>',
    'car': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>',
    'bolt': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/></svg>',
    'scene': '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>',
  }

  // Additional icons
  icons['bullseye'] = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="2"/></svg>'
  icons['video'] = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>'
  icons['play'] = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>'
  icons['youtube'] = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z"/></svg>'

  // Default icon per type
  if (icon && icons[icon]) return icons[icon]
  if (type === 'scene') return icons['scene']
  if (type === 'video') return icons['video']
  if (type === 'link') return icons['link']
  return icons['info']
}

// Sanitize HTML content (basic)
function sanitize(html: string): string {
  // Strip script tags and event handlers
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\son\w+\s*=/gi, ' data-blocked=')
}

function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}
</script>

<style scoped>
.hotspot-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 12;
  overflow: hidden;
}

.hotspot-marker {
  pointer-events: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: transform 0.15s;
}

.hotspot-marker:hover {
  transform: translate(-50%, -50%) scale(1.15) !important;
  z-index: 100;
}

.hotspot-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  transition: all 0.2s;
}

/* Type-specific colors */
.hotspot-scene .hotspot-icon {
  background: rgba(59, 130, 246, 0.85);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.6);
}

.hotspot-info .hotspot-icon {
  background: rgba(245, 158, 11, 0.85);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.6);
}

.hotspot-link .hotspot-icon {
  background: rgba(16, 185, 129, 0.85);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.6);
}

.hotspot-video .hotspot-icon {
  background: rgba(239, 68, 68, 0.85);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.6);
}

/* Pulse animation for scene hotspots */
.hotspot-scene .hotspot-icon::after {
  content: '';
  position: absolute;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid rgba(59, 130, 246, 0.5);
  animation: hotspot-pulse 2s ease-out infinite;
}

@keyframes hotspot-pulse {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.8); opacity: 0; }
}

.hotspot-label {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  backdrop-filter: blur(4px);
}

.hotspot-active .hotspot-icon {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.4), 0 4px 16px rgba(0, 0, 0, 0.5);
}
</style>

<style>
/* Popover backdrop + centered popover */
.hotspot-popover-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.15s ease;
  pointer-events: auto;
}

.hotspot-popover {
  position: relative;
  background: #1e293b;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  min-width: 200px;
  max-width: 560px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  color: white;
  animation: popoverIn 0.15s ease;
  margin: 16px;
  pointer-events: auto;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes popoverIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px 0;
  min-height: 32px;
}

.popover-title {
  font-weight: 600;
  font-size: 14px;
}

.popover-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.popover-close:hover {
  color: white;
}

.popover-body {
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
}

.popover-body img {
  max-width: 100%;
  border-radius: 6px;
  margin: 4px 0;
}

.popover-body iframe {
  max-width: 100%;
  border-radius: 6px;
  margin: 4px 0;
  border: none;
}

.popover-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px 12px;
  color: #60a5fa;
  font-size: 12px;
  text-decoration: none;
  word-break: break-all;
}

.popover-link:hover {
  color: #93bbfc;
  text-decoration: underline;
}

/* ── Hotspot gazing state (VR dwell in progress) ── */
.hotspot-gazing .hotspot-icon {
  transform: scale(1.25);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.6), 0 0 16px rgba(255, 255, 255, 0.3);
  transition: transform 0.15s, box-shadow 0.15s;
}

/* ── VR crosshair + dwell ring ── */
.vr-crosshair {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  pointer-events: none;
  z-index: 30;
}

.vr-dwell-ring {
  position: absolute;
  inset: 0;
  width: 40px;
  height: 40px;
}

.dwell-track {
  fill: none;
  stroke: rgba(255, 255, 255, 0.2);
  stroke-width: 2.5;
}

.dwell-fill {
  fill: none;
  stroke: white;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-dasharray: 106.8; /* 2π × 17 */
  transform-origin: center;
  transform: rotate(-90deg);
  filter: drop-shadow(0 0 4px rgba(255,255,255,0.7));
}

.crosshair-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 0 6px rgba(0,0,0,0.6);
}
</style>
